import { Static, Type } from '@sinclair/typebox';
import { ProCustomerResponseSchema, phoneNumberPattern } from './professional';
import { ProCustomerCustomFieldValueInputSchema } from './pro-customer-custom-field';
import {
  ProCustomerDuplicateSignal,
  ProCustomerDuplicateTier,
  ProCustomerMergeWarning,
} from './translation_namespace/pro_customer_duplicate_namespace';

/**
 * Fusion de fiches clients — contrat partagé backend ↔ frontend.
 *
 * La détection tourne À LA DEMANDE (projection bornée en mémoire, cf.
 * `duplicate-matcher.ts` côté backend) : rien n'est persisté hormis les paires
 * ignorées (`pro_customer_ignored_duplicate`) et la trace de fusion
 * (`pro_customer.merged_into_pro_customer_id` / `merged_at`).
 */

// ── Bornes du moteur (source de vérité unique, appliquées backend) ──────────

/** Seuil de similarité trigramme (Jaccard, sémantique pg_trgm) du tier TO_CHECK. */
export const PRO_CUSTOMER_DUPLICATE_NAME_SIMILARITY_THRESHOLD = 0.5;
/**
 * En-dessous de cette longueur de clé nom (points de code, espaces exclus) — ou
 * pour un nom sans écriture latine — la ressemblance trigramme n'a pas de sens
 * statistique : seule l'égalité exacte des clés normalisées est retenue.
 */
export const PRO_CUSTOMER_DUPLICATE_MIN_NAME_KEY_LENGTH = 6;
/** Membres affichés au maximum par groupe (au-delà : `is_truncated`). */
export const PRO_CUSTOMER_DUPLICATE_MAX_GROUP_MEMBERS = 10;
/**
 * Un identifiant exact (email / téléphone) partagé par plus de N fiches est un
 * placeholder (noreply@, numéro d'accueil…) : le bloc est ignoré et compté,
 * jamais proposé — garde internationale sans liste de mots.
 */
export const PRO_CUSTOMER_DUPLICATE_MAX_IDENTIFIER_BLOCK_SIZE = 25;
/**
 * Au-delà de N voisins « au nom ressemblant », un nom n'identifie plus
 * personne : ce sont des libellés génériques (« Client 12 », « Passage »,
 * lignes d'un import sans nom). On ne propose alors AUCUNE ressemblance pour
 * cette fiche — les tiers fondés sur un identifiant (email, téléphone) ne sont
 * jamais touchés.
 */
export const PRO_CUSTOMER_DUPLICATE_MAX_SIMILAR_NAME_NEIGHBOURS = 3;

/**
 * Un trigramme porté par plus de fiches que ce plafond est trop fréquent pour
 * discriminer : on cesse de parcourir sa liste d'occurrences. Comme le coût du
 * scan EST la somme des longueurs parcourues, ce nombre pilote directement le
 * temps de calcul.
 *
 * 200 est mesuré, pas choisi au jugé (25 000 fiches, base réaliste) :
 *   - pire cas 3 341 ms → 1 167 ms, pour des propositions STRICTEMENT identiques ;
 *   - sur une base à noms variés, le rappel MONTE (36 % → 50 % des doublons connus)
 *     car ignorer un trigramme abaisse aussi le seuil de compensation, si bien que
 *     de vraies paires cessent d'être écartées trop tôt ;
 *   - 1 000 et 500 donnent exactement le même résultat : au-delà de 300 le
 *     plafond ne filtre plus rien, il ne servait à rien ;
 *   - en dessous de 100 le rappel décroche (des paires ne sont plus engendrées).
 *
 * Ne pas le remonter « par prudence » : c'est l'inverse qui est vrai.
 * Les tiers fondés sur un identifiant (email, téléphone) n'en dépendent pas.
 */
export const PRO_CUSTOMER_DUPLICATE_MAX_TRIGRAM_POSTING = 200;
/**
 * Clé d'erreur métier du 404 « fiche fusionnée » (getProCustomerById /
 * putProCustomer). Constante partagée : le frontend route la redirection des
 * liens profonds dessus, sans littéral en dur.
 */
export const API_ERROR_CUSTOMER_MERGED = 'CUSTOMER_MERGED';

/** TTL du cache de scan par pro (validé en plus par empreinte count/max(updated_at)). */
export const PRO_CUSTOMER_DUPLICATE_SCAN_CACHE_TTL_MS = 10 * 60_000;

// ── Schémas ─────────────────────────────────────────────────────────────────

/**
 * Fiche candidate telle qu'affichée dans les propositions. Contrat séparé de
 * `ProCustomerResponseSchema` (qui pilote l'export CSV + le parse Typesense) :
 * on n'y ajoute PAS de champs. Tous les affichages sont formatés côté backend
 * (règles Dates/Prix + D5 international) : `customer_since_formatted` via
 * transformToSchema, `phone_number_formatted` (national si même pays que le
 * pro, international sinon) et `birthday_formatted` calculés par le service.
 */
export const ProCustomerDuplicateCandidateSchema = Type.Object({
  pro_customer_id: Type.Number(),
  firstname: Type.Optional(Type.String()),
  lastname: Type.Optional(Type.String()),
  email: Type.Optional(Type.String()),
  phone_number: Type.Optional(Type.String()),
  phone_number_formatted: Type.Optional(Type.String()),
  birthday_formatted: Type.Optional(Type.String()),
  /** « Client depuis » (`customer_since`, date métier — pas `created_at`, technique). */
  customer_since_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'customer_since' } })),
  /** Jour (`YYYY-MM-DD`, fuseau du pro) de `customer_since`, pour la LOGIQUE seulement (client
   * le plus ancien = survivant par défaut — l'ordre des ids ment pour les fiches migrées v1 ou
   * importées ; même jour → plus petit id). Au jour et non à l'instant : les valeurs mêlent débuts
   * de jour (saisie, import) et instants (reprise de created_at). L'affichage reste
   * `customer_since_formatted` (règle Dates). */
  customer_since_iso: Type.Optional(Type.String()),
  appointment_count: Type.Number(),
  sale_count: Type.Number(),
  has_stripe_customer: Type.Boolean(),
});
export type ProCustomerDuplicateCandidateObject = Static<typeof ProCustomerDuplicateCandidateSchema>;

export const ProCustomerDuplicatePairSchema = Type.Object({
  /** `<low>-<high>` — identité stable de la paire côté UI et table d'ignore. */
  pair_key: Type.String(),
  low_pro_customer_id: Type.Number(),
  high_pro_customer_id: Type.Number(),
  tier: Type.Enum(ProCustomerDuplicateTier),
  signals: Type.Array(Type.Enum(ProCustomerDuplicateSignal)),
  /** Présent uniquement quand la ressemblance trigramme a joué (tier TO_CHECK). */
  name_similarity: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
});
export type ProCustomerDuplicatePairObject = Static<typeof ProCustomerDuplicatePairSchema>;

/** Composante connexe (union-find) de paires non ignorées. */
export const ProCustomerDuplicateGroupSchema = Type.Object({
  /** `g:<plus petit id membre>` — stable tant que le groupe existe. */
  group_key: Type.String(),
  /** Meilleur tier parmi les paires du groupe. */
  tier: Type.Enum(ProCustomerDuplicateTier),
  is_truncated: Type.Boolean(),
  customers: Type.Array(ProCustomerDuplicateCandidateSchema),
  pairs: Type.Array(ProCustomerDuplicatePairSchema),
});
export type ProCustomerDuplicateGroupObject = Static<typeof ProCustomerDuplicateGroupSchema>;

export const ListProCustomerDuplicateGroupsResponseSchema = Type.Object({
  has_elements: Type.Boolean(),
  /** Nombre TOTAL de groupes (toutes pages). */
  total: Type.Number(),
  total_pairs: Type.Number(),
  sure_count: Type.Number(),
  probable_count: Type.Number(),
  to_check_count: Type.Number(),
  /**
   * true si la base DÉPASSE la capacité d'analyse : des fiches n'ont jamais été
   * comparées, le pro rate peut-être de vrais doublons. À ne pas confondre avec
   * `has_ignored_blocks` — un drapeau unique mélangeait une vraie limite et un
   * comportement correct, et alarmait pour le second.
   */
  is_truncated: Type.Boolean(),
  /**
   * true si des lignes sans identifiant exploitable ont été VOLONTAIREMENT
   * écartées (import incomplet : trente fiches sur la même adresse de repli).
   * Ce n'est pas une limite, c'est le moteur qui évite des centaines de fausses
   * propositions — mais ça explique au pro pourquoi ces fiches n'apparaissent pas.
   */
  has_ignored_blocks: Type.Boolean(),
  items: Type.Array(ProCustomerDuplicateGroupSchema),
});
export type ListProCustomerDuplicateGroupsResponseObject = Static<typeof ListProCustomerDuplicateGroupsResponseSchema>;

/** Compteurs pour la bannière /customers — même scan, réponse minimale. */
export const ProCustomerDuplicateSummaryResponseSchema = Type.Object({
  enabled: Type.Boolean(),
  total_groups: Type.Number(),
  total_pairs: Type.Number(),
  sure_count: Type.Number(),
  probable_count: Type.Number(),
  to_check_count: Type.Number(),
  /** Cf. `ListProCustomerDuplicateGroupsResponseSchema` : capacité dépassée. */
  is_truncated: Type.Boolean(),
  /** Cf. `ListProCustomerDuplicateGroupsResponseSchema` : blocs volontairement écartés. */
  has_ignored_blocks: Type.Boolean(),
});
export type ProCustomerDuplicateSummaryResponseObject = Static<typeof ProCustomerDuplicateSummaryResponseSchema>;

/** Une fiche qui ressemble à la fiche sondée (encart fiche client + alerte création). */
export const ProCustomerDuplicateMatchSchema = Type.Object({
  tier: Type.Enum(ProCustomerDuplicateTier),
  signals: Type.Array(Type.Enum(ProCustomerDuplicateSignal)),
  name_similarity: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  customer: ProCustomerDuplicateCandidateSchema,
  /**
   * Fiche que la fusion propose de CONSERVER : la plus ancienne des deux, et à
   * dates égales celle au plus petit identifiant. Calculé côté serveur pour que
   * l'encart d'une fiche et la liste des doublons proposent la MÊME fiche — sans
   * ce champ, l'encart retombait sur « le plus petit identifiant », ce qui n'est
   * pas la même chose dès que l'ordre de création ne suit pas celui des ids
   * (fiches migrées de la v1).
   */
  default_survivor_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type ProCustomerDuplicateMatchObject = Static<typeof ProCustomerDuplicateMatchSchema>;

export const ProCustomerDuplicateMatchesResponseSchema = Type.Object({
  matches: Type.Array(ProCustomerDuplicateMatchSchema),
});
export type ProCustomerDuplicateMatchesResponseObject = Static<typeof ProCustomerDuplicateMatchesResponseSchema>;

/**
 * Choix champ par champ du pro dans l'aperçu de fusion — chaque clé ABSENTE
 * suit la résolution automatique (survivant rempli sinon perdant ; commentaires
 * bout à bout). Champs texte : la valeur fournie fait foi (venue de la fiche A,
 * de la fiche B ou éditée librement), chaîne vide = vider le champ.
 * Anniversaire / adresse (structurés) : choix du CÔTÉ uniquement.
 * Consentements : jamais de ré-opt-in — un `true` est écrasé si l'une des deux
 * fiches porte un refus (garde backend, identique à putProCustomer).
 * Volontairement NON couverts : tags (toujours l'union), refus (toujours OR),
 * Stripe / import (traçabilité).
 */
export const ProCustomerMergeFieldOverridesSchema = Type.Object({
  firstname: Type.Optional(Type.String({ maxLength: 255 })),
  lastname: Type.Optional(Type.String({ maxLength: 255 })),
  gender: Type.Optional(Type.String({ maxLength: 255 })),
  /** Validé côté service (formatEmail) : le format TypeBox `email` refuserait la chaîne vide « vider ». */
  email: Type.Optional(Type.String({ maxLength: 255 })),
  // `meta.input_type` n'est pas cosmétique : c'est lui qui déclenche le contrôle
  // libphonenumber du validateur backend. Sans lui, ce champ acceptait des
  // numéros que la création d'un client refuse (« +3361234 », « +9991234567 ») —
  // l'écran les bloquait déjà, mais un appel direct à l'API passait outre.
  phone_number: Type.Optional(
    Type.String({ pattern: phoneNumberPattern, maxLength: 15, meta: { input_type: 'phone_number' } }),
  ),
  comment: Type.Optional(Type.String({ maxLength: 2000 })),
  birthday_from: Type.Optional(Type.Union([Type.Literal('survivor'), Type.Literal('loser')])),
  address_from: Type.Optional(Type.Union([Type.Literal('survivor'), Type.Literal('loser')])),
  send_marketing_email: Type.Optional(Type.Boolean()),
  send_marketing_sms: Type.Optional(Type.Boolean()),
  /**
   * Choix du pro sur les champs personnalisés. Sémantique identique aux
   * champs texte : la valeur remplace la résolution automatique, et la chaîne
   * vide VIDE le champ. Les ids inconnus du pro sont ignorés côté service.
   */
  custom_fields: Type.Optional(Type.Array(ProCustomerCustomFieldValueInputSchema)),
});
export type ProCustomerMergeFieldOverridesObject = Static<typeof ProCustomerMergeFieldOverridesSchema>;

/** Corps commun de `POST merge/preview` et `POST merge`. */
export const ProCustomerMergeRequestSchema = Type.Object({
  survivor_pro_customer_id: Type.Integer({ minimum: 1 }),
  loser_pro_customer_id: Type.Integer({ minimum: 1 }),
  field_overrides: Type.Optional(ProCustomerMergeFieldOverridesSchema),
});
export type ProCustomerMergeRequestObject = Static<typeof ProCustomerMergeRequestSchema>;

/**
 * Fiche « côté » de l'aperçu : la fiche complète + les stats d'impact.
 * `Type.Composite` (schéma plat) et non `Type.Intersect` — cf. rationale de
 * `ListProCustomerSalesResponseSchema` (pro_payment.ts).
 */
export const ProCustomerMergeSideSchema = Type.Composite([
  ProCustomerResponseSchema,
  Type.Object({
    phone_number_formatted: Type.Optional(Type.String()),
    birthday_formatted: Type.Optional(Type.String()),
    // `customer_since_formatted` est hérité de ProCustomerResponseSchema.
    customer_since_iso: Type.Optional(Type.String()),
    appointment_count: Type.Number(),
    reservation_count: Type.Number(),
    voucher_count: Type.Number(),
    sale_count: Type.Number(),
    has_stripe_customer: Type.Boolean(),
  }),
]);
export type ProCustomerMergeSideObject = Static<typeof ProCustomerMergeSideSchema>;

export const ProCustomerMergeMovedCountsSchema = Type.Object({
  appointments: Type.Number(),
  reservations: Type.Number(),
  vouchers: Type.Number(),
  payments: Type.Number(),
  notification_activities: Type.Number(),
  notification_logs: Type.Number(),
});
export type ProCustomerMergeMovedCountsObject = Static<typeof ProCustomerMergeMovedCountsSchema>;

export const ProCustomerMergePreviewResponseSchema = Type.Object({
  survivor: ProCustomerMergeSideSchema,
  loser: ProCustomerMergeSideSchema,
  /** Fiche résultante (virtuelle — rien n'est écrit par le preview). */
  merged: ProCustomerResponseSchema,
  /** Tier/signaux de la paire quand le moteur la propose (absent pour une fusion manuelle libre). */
  tier: Type.Optional(Type.Enum(ProCustomerDuplicateTier)),
  signals: Type.Array(Type.Enum(ProCustomerDuplicateSignal)),
  warnings: Type.Array(Type.Enum(ProCustomerMergeWarning)),
  moved_counts: ProCustomerMergeMovedCountsSchema,
});
export type ProCustomerMergePreviewResponseObject = Static<typeof ProCustomerMergePreviewResponseSchema>;

/** `survivor` a la MÊME shape que la liste/getById → le front patche la liste tel quel. */
export const ProCustomerMergeResponseSchema = Type.Object({
  survivor: ProCustomerResponseSchema,
  loser_pro_customer_id: Type.Number(),
});
export type ProCustomerMergeResponseObject = Static<typeof ProCustomerMergeResponseSchema>;

export const ProCustomerIgnoreDuplicateRequestSchema = Type.Object({
  pro_customer_id_a: Type.Integer({ minimum: 1 }),
  pro_customer_id_b: Type.Integer({ minimum: 1 }),
});
export type ProCustomerIgnoreDuplicateRequestObject = Static<typeof ProCustomerIgnoreDuplicateRequestSchema>;
